// Filename: ClientView.js
// Description: This component renders the details of a specific client.

// REACT, HOOKS, AND LIBRARIES
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
// Importing React Bootstrap components
import { Modal, Button } from "react-bootstrap";
import Cookies from "js-cookie";
import { v4 as uuidv4 } from "uuid";
// Importing Font Awesome for icon support
import 'font-awesome/css/font-awesome.min.css';

// Importing example profile pictures (kept for original file context)
// import profilePic1 from "./../../ExampleProfile1.jpg";
// import profilePic2 from "./../../ExampleProfile2.jpg";

// HELPER FUNCTION: Formats the phone number for display
const formatPhoneNumber = (areaCode, telPrefix, lineNum) => {
    if (!areaCode || !telPrefix || !lineNum) return '';
    return `(${areaCode}) ${telPrefix}-${lineNum}`;
};

// HELPER FUNCTION: Extracts/formats client data for the UPDATE FORM (autofill)
const getInitialUpdateInfo = (clientData) => {
    // Safely extract and combine phone number parts into a single string (10 digits)
    const getPhoneNumberString = (c) => `${c.areaCode || ''}${c.telPrefix || ''}${c.lineNum || ''}`;

    return {
        clientID: clientData.clientID || "",
        firstName: clientData.firstName || "",
        middleName: clientData.middleName || "",
        lastName: clientData.lastName || "",
        // CRUCIAL: Ensure DOB is in YYYY-MM-DD format for date input autofill
        DOB: clientData.DOB ? new Date(clientData.DOB).toISOString().split("T")[0] : "",
        photo: clientData.photo || "",
        email: clientData.email || "",
        addressLine1: clientData.addressLine1 || "",
        addressLine2: clientData.addressLine2 || "",
        city: clientData.city || "",
        state: clientData.state || "",
        zip: clientData.zip || "",
        // Combine phone parts into a single string for update input
        phoneNumber: getPhoneNumberString(clientData),
    };
};

// --- START: EditHouseholdModal (Refactored to React-Bootstrap Modal) ---

// Component to edit the household members
const EditHouseholdModal = ({ clientID, show, onHide, onHouseholdUpdate }) => {
    const navigate = useNavigate();
    const [household, setHousehold] = useState([]);
    // State for new member inputs
    const [newMemberFirstName, setNewMemberFirstName] = useState("");
    const [newMemberLastName, setNewMemberLastName] = useState("");
    const [newMemberDateOfBirth, setNewMemberDateOfBirth] = useState("");

    // Get the household members when the modal opens
    useEffect(() => {
        if (!clientID || !show) {
            setHousehold([]);
            setNewMemberFirstName("");
            setNewMemberLastName("");
            setNewMemberDateOfBirth("");
            return;
        }

        const fetchHouseholdMembers = async () => {
            try {
                const sessionID = Cookies.get("sessionID");
                if (!sessionID) {
                    alert("Session expired. Please log in again.");
                    navigate("/SignIn");
                    return;
                }

                const response = await axios.get(
                    `${process.env.REACT_APP_BACKURL}/api/foodPantry/household/client?clientID=${clientID}`,
                    {
                        withCredentials: true,
                        headers: { Authorization: `Bearer ${sessionID}`, "Content-Type": "application/json", },
                    }
                );

                if (response.data.familyMembers && Array.isArray(response.data.familyMembers)) {
                    setHousehold(response.data.familyMembers);
                } else {
                    console.error("Unexpected response format:", response.data);
                    setHousehold([]);
                }
            } catch (error) {
                console.error("Error fetching household members:", error);
            }
        };

        fetchHouseholdMembers();
    }, [clientID, navigate, show]);

    // Delete a household member
    const handleDeleteMember = async (memberClientIDToDelete) => {
        if (!window.confirm("Are you sure you want to delete this member?")) {
            return;
        }
        try {
            const sessionID = Cookies.get("sessionID");
            if (!sessionID) {
                alert("Session expired. Please log in again.");
                navigate("/SignIn");
                return;
            }

            await axios.delete(
                `${process.env.REACT_APP_BACKURL}/api/foodPantry/household/client?clientID=${memberClientIDToDelete}`,
                {
                    withCredentials: true,
                    headers: { Authorization: `Bearer ${sessionID}`, "Content-Type": "application/json", },
                }
            );

            setHousehold((prevHousehold) =>
                prevHousehold.filter((member) => member.clientID !== memberClientIDToDelete)
            );
            // ACTION: Trigger refresh of household-dependent views in the parent wrapper
            if (onHouseholdUpdate) {
                onHouseholdUpdate();
            }
        } catch (error) {
            console.error("Error deleting household member:", error);
            alert("Error deleting household member. Please try again.");
        }
    };

    const handleNewMemberFirstNameChange = (e) => {
        setNewMemberFirstName(e.target.value);
    };
    const handleNewMemberLastNameChange = (e) => {
        setNewMemberLastName(e.target.value);
    };
    const handleNewMemberDateOfBirthChange = (e) => {
        setNewMemberDateOfBirth(e.target.value);
    };

    // Add a household member
    const handleAddMember = async (e) => {
        // Prevent default form submission if called from a form
        if (e) e.preventDefault();
        if (!newMemberFirstName || !newMemberLastName || !newMemberDateOfBirth) {
            alert("Please enter the first name, last name, and date of birth of the member to add.");
            return;
        }
        try {
            const sessionID = Cookies.get("sessionID");
            if (!sessionID) {
                alert("Session expired. Please log in again.");
                navigate("/SignIn");
                return;
            }

            await axios.post(
                `${process.env.REACT_APP_BACKURL}/api/foodPantry/household/client`,
                { "clientID": clientID, "newMemberFirstName": newMemberFirstName, "newMemberLastName": newMemberLastName, "newMemberDateOfBirth": newMemberDateOfBirth },
                {
                    withCredentials: true,
                    headers: { Authorization: `Bearer ${sessionID}`, "Content-Type": "application/json", },
                }
            );
            // Clear input fields on successful add
            setNewMemberFirstName("");
            setNewMemberLastName("");
            setNewMemberDateOfBirth("");

            // ACTION: Trigger refresh of household-dependent views in the parent wrapper
            if (onHouseholdUpdate) {
                onHouseholdUpdate();
            }

            // Manually re-fetch household to update the modal list without closing
            const response = await axios.get(
                `${process.env.REACT_APP_BACKURL}/api/foodPantry/household/client?clientID=${clientID}`,
                {
                    withCredentials: true,
                    headers: { Authorization: `Bearer ${sessionID}`, "Content-Type": "application/json", },
                }
            );

            if (response.data.familyMembers && Array.isArray(response.data.familyMembers)) {
                setHousehold(response.data.familyMembers);
            }

        } catch (error) {
            console.error("Error adding household member:", error);
            alert("Error adding household member. Please try again.");
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            aria-labelledby="editHouseholdModalLabel"
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title id="editHouseholdModalLabel">
                    Edit Household <i className="fa fa-users text-primary"></i>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Scrollable table: table-responsive */}
                <div className="table-responsive px-2">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th scope="col">Picture</th>
                                <th scope="col">First</th>
                                <th scope="col">Last</th>
                                <th scope="col" style={{ minWidth: '100px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.isArray(household) && household.length > 0 ? (
                                household.map((member) => (
                                    <tr key={member.clientID}>
                                        <td>
                                            <img
                                                src={member.photo}
                                                alt="profile picture"
                                                style={{
                                                    width: "64px",
                                                    height: "64px",
                                                    objectFit: "cover",
                                                }}
                                                className="rounded-circle"
                                            />
                                        </td>
                                        <td>{member.firstName}</td>
                                        <td>{member.lastName}</td>
                                        <td>
                                            <Button
                                                variant="danger"
                                                onClick={() => handleDeleteMember(member.clientID)}
                                                size="sm"
                                            >
                                                <i className="fa fa-trash me-1"></i> Delete
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="text-center">
                                        No household members found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Form to add a new household member - Now wrapped in a <form> for Enter key submission */}
                <form onSubmit={handleAddMember} className="row g-2 mt-3 pb-3 px-2 align-items-end">
                    {/* INPUT FIELD: First Name */}
                    <div className="col-md-3 form-floating">
                        <input
                            type="text"
                            className="form-control"
                            id="firstName"
                            placeholder="First Name"
                            value={newMemberFirstName}
                            onChange={handleNewMemberFirstNameChange}
                            required
                        />
                        <label htmlFor="firstName">First Name</label>
                    </div>
                    {/* INPUT FIELD: Last Name */}
                    <div className="col-md-3 form-floating">
                        <input
                            type="text"
                            className="form-control"
                            id="lastName"
                            placeholder="Last Name"
                            value={newMemberLastName}
                            onChange={handleNewMemberLastNameChange}
                            required
                        />
                        <label htmlFor="lastName">Last Name</label>
                    </div>
                    {/* INPUT FIELD: Date of Birth */}
                    <div className="col-md-3 form-floating">
                        <input
                            type="date"
                            className="form-control"
                            id="dateOfBirth"
                            placeholder="Date of Birth"
                            value={newMemberDateOfBirth}
                            onChange={handleNewMemberDateOfBirthChange}
                            required
                        />
                        <label htmlFor="dateOfBirth">Date of Birth</label>
                    </div>

                    <div className="col-md-3 d-grid">
                        <Button
                            variant="primary"
                            type="submit" // NEW: Set type to submit for Enter key functionality
                        >
                            <i className="fa fa-plus-circle me-1"></i> Add Member
                        </Button>
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    );
};

// --- END: EditHouseholdModal ---


// --- START: Embedded Modal Definitions for ClientView ---

const LogVisitModal = ({ show, onHide, visitInfo, serviceInfo, organizationName, handleChange, handleServiceChange, handleLogVisit, isRootUser, organizations, handleOrganizationChange }) => (
    <Modal show={show} onHide={onHide} size="lg" aria-labelledby="logVisitModalLabel" centered>
        <Modal.Header closeButton>
            <Modal.Title id="logVisitModalLabel">
                Log New Visit <i className="fa fa-book-medical text-primary"></i>
            </Modal.Title>
        </Modal.Header>
        <Modal.Body>
            {/* Form is set up with onSubmit={handleLogVisit} for Enter key submission */}
            <form onSubmit={handleLogVisit}>
                <div className="row g-2">
                    {/* Organization and Service Type */}
                    <div className="col-lg-6 col-md-12">
                        {/* CONDITIONAL RENDERING FOR ORGANIZATION FIELD */}
                        {isRootUser ? (
                            // Super Admin: Drop-down menu
                            <div className="form-floating mb-3">
                                <select
                                    id="txtOrg"
                                    className="form-select form-select-floating-fix pb-0 pt-3 ps-3"
                                    value={organizationName}
                                    onChange={handleOrganizationChange}
                                >
                                    <option value="Select Organization">Select Organization</option>
                                    {organizations.map((org) => (
                                        <option key={org.id} value={org.name}>
                                            {org.name}
                                        </option>
                                    ))}
                                </select>
                                <label htmlFor="txtOrg" className="form-label"> Organization</label>
                            </div>
                        ) : (
                            // Org Admin: Disabled text input
                            <div className="form-floating mb-3">
                                <input 
                                    id="txtOrg" 
                                    type="text" 
                                    className="form-control" 
                                    value={organizationName} 
                                    disabled 
                                    placeholder="Organization Name" 
                                />
                                <label htmlFor="txtOrg" className="form-label"> Organization</label>
                            </div>
                        )}
                    </div>
                    <div className="col-lg-6 col-md-12">
                {/* INPUT FIELD: Service Type (Select) */}
                    <div className="form-floating mb-3">
                        <select 
                            id="txtServiceType" 
                            className="form-select form-select-floating-fix pb-0 pt-3 ps-3"
                            name="serviceType" 
                            value={serviceInfo.serviceType} 
                            onChange={handleServiceChange}
                        >
                            <option value="Food Pantry">Food Pantry</option>
                            <option value="Financial Assistance">Financial Assistance</option>
                            <option value="Medical Services">Medical Services</option>
                            <option value="Transportation Services">Transportation Services</option>
                            <option value="Laundry Services">Laundry Services</option>
                        </select>
                        <label htmlFor="txtServiceType" className="form-label"> Service Type</label>
                    </div>
                </div>

                    {/* Visit Details */}
                    <div className="col-lg-6 col-md-12">
                        {/* INPUT FIELD: Date */}
                        <div className="form-floating mb-3">
                            <input id="txtDate" type="date" className="form-control" name="date" value={visitInfo.date} onChange={handleChange} required />
                            <label htmlFor="txtDate" className="form-label"> Date of Visit</label>
                        </div>
                    </div>
                    <div className="col-lg-6 col-md-12">
                        {/* INPUT FIELD: Reason */}
                        <div className="form-floating mb-3">
                            <input id="txtReason" type="text" placeholder="Reason for Visit" className="form-control" name="reason" value={visitInfo.reason} onChange={handleChange} required />
                            <label htmlFor="txtReason" className="form-label"> Reason</label>
                        </div>
                    </div>
                    <div className="col-12">
                        {/* INPUT FIELD: Notes (Textarea) */}
                        <div className="form-floating mb-3">
                            <textarea id="txtNotes" placeholder="Notes" className="form-control" name="notes" value={visitInfo.notes} onChange={handleChange} style={{ height: '100px' }}></textarea>
                            <label htmlFor="txtNotes" className="form-label"> Notes</label>
                        </div>
                    </div>

                    <div className="col-lg-6 col-md-12">
                        {/* INPUT FIELD: Outcome */}
                        <div className="form-floating mb-3">
                            <input id="txtOutcome" type="text" placeholder="Outcome" className="form-control" name="outcome" value={visitInfo.outcome} onChange={handleChange} />
                            <label htmlFor="txtOutcome" className="form-label"> Outcome</label>
                        </div>
                    </div>

                    <div className="col-lg-6 col-md-12">
                        {/* INPUT FIELD: Follow-up Date */}
                        <div className="form-floating mb-3">
                            <input id="txtFollowUp" type="date" placeholder="Follow-up Date"
                                className="form-control" name="followUp" value={visitInfo.followUp} onChange={handleChange} />
                            <label htmlFor="txtFollowUp" className="form-label"> Follow-Up Date (Optional)</label>
                        </div>
                    </div>
                </div>

                {/* Hidden submit button to ensure Enter keypress inside the modal body triggers form submission */}
                <button type="submit" style={{ display: 'none' }} aria-hidden="true"></button>
            </form>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end">
            <Button variant="primary" onClick={handleLogVisit}>
                <i className="fa fa-check-circle me-1"></i> Log Visit!
            </Button>
        </Modal.Footer>
    </Modal>
);

const EditClientModal = ({ show, onHide, updateInfo, handleUpdate, handleSave }) => (
    <Modal show={show} onHide={onHide} aria-labelledby="editClientModalLabel" centered>
        <Modal.Header closeButton>
            <Modal.Title id="editClientModalLabel">
                Edit Client <i className="fa fa-pen-to-square text-primary"></i>
            </Modal.Title>
        </Modal.Header>
        <Modal.Body>

            {/* onSubmit={handleSave} enables Enter key submission */}
            <form onSubmit={handleSave}>
                <div className="row g-2">
                    {/* INPUT FIELD: First Name */}
                    <div className="col-12 col-lg-6">
                        <div className="form-floating mb-3">
                            <input id="txtFirstName" type="text" className="form-control" placeholder="First Name" name="firstName" value={updateInfo.firstName} onChange={handleUpdate} />
                            <label htmlFor="txtFirstName" className="form-label"> First Name</label>
                        </div>
                    </div>
                    {/* INPUT FIELD: Last Name */}
                    <div className="col-12 col-lg-6">
                        <div className="form-floating mb-3">
                            <input id="txtLastName" type="text" className="form-control" placeholder="Last Name" name="lastName" value={updateInfo.lastName} onChange={handleUpdate} />
                            <label htmlFor="txtLastName" className="form-label"> Last Name</label>
                        </div>
                    </div>
                    {/* INPUT FIELD: Phone Number */}
                    <div className="col-12 col-lg-6">
                        <div className="form-floating mb-3">
                            <input id="txtPhoneUpdate" type="tel" className="form-control" placeholder="(555) 123-4567" name="phoneNumber" value={updateInfo.phoneNumber} onChange={handleUpdate} maxLength={10} title="Phone number must be 10 digits." />
                            <label htmlFor="txtPhoneUpdate" className="form-label"> Phone Number (10 digits)</label>
                        </div>
                    </div>
                    {/* INPUT FIELD: Date of Birth */}
                    <div className="col-12 col-lg-6">
                        <div className="form-floating mb-3">
                            <input id="txtDOBUpdate" type="date" className="form-control" placeholder="01-01-2000" name="DOB" value={updateInfo.DOB} onChange={handleUpdate} />
                            <label htmlFor="txtDOBUpdate" className="form-label"> Date of Birth</label>
                        </div>
                    </div>
                    {/* INPUT FIELD: Email */}
                    <div className="col-12">
                        <div className="form-floating mb-3">
                            <input id="txtEmailUpdate" type="email" placeholder="jdoe@email.com" className="form-control" name="email" value={updateInfo.email} onChange={handleUpdate} />
                            <label htmlFor="txtEmailUpdate" className="form-label"> Email</label>
                        </div>
                    </div>
                    {/* INPUT FIELD: Address Line 1 */}
                    <div className="col-12">
                        <div className="form-floating mb-3">
                            <input id="txtStreetAddress1Update" type="text" placeholder="123 Main St." className="form-control" name="addressLine1" value={updateInfo.addressLine1} onChange={handleUpdate} />
                            <label htmlFor="txtStreetAddress1Update" className="form-label"> Street Address Line 1</label>
                        </div>
                    </div>
                    {/* INPUT FIELD: Address Line 2 */}
                    <div className="col-12">
                        <div className="form-floating mb-3">
                            <input id="txtStreetAddress2Update" type="text" placeholder="Apt. 1" className="form-control" name="addressLine2" value={updateInfo.addressLine2} onChange={handleUpdate} />
                            <label htmlFor="txtStreetAddress2Update" className="form-label"> Street Address Line 2</label>
                        </div>
                    </div>

                    {/* Address Components */}
                    <div className="col-12 col-lg-4">
                        <div className="form-floating mb-3">
                            <input id="txtCityUpdate" type="text" placeholder="City" className="form-control" name="city" value={updateInfo.city} onChange={handleUpdate} />
                            <label htmlFor="txtCityUpdate" className="form-label"> City</label>
                        </div>
                    </div>
                    <div className="col-12 col-lg-4">
                        <div className="form-floating mb-3">
                            <input id="txtStateUpdate" type="text" placeholder="State" className="form-control" name="state" value={updateInfo.state} onChange={handleUpdate} />
                            <label htmlFor="txtStateUpdate" className="form-label"> State</label>
                        </div>
                    </div>
                    <div className="col-12 col-lg-4">
                        <div className="form-floating mb-3">
                            <input id="txtZipUpdate" type="text" placeholder="Zip Code" className="form-control" name="zip" value={updateInfo.zip} onChange={handleUpdate} />
                            <label htmlFor="txtZipUpdate" className="form-label"> Zip Code</label>
                        </div>
                    </div>
                </div>
                {/* Hidden submit button to ensure Enter keypress inside the modal body triggers form submission */}
                <button type="submit" style={{ display: 'none' }} aria-hidden="true"></button>
            </form>

        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end">
            <Button variant="primary" onClick={handleSave}>
                <i className="fa fa-floppy-o me-1"></i> Save Changes
            </Button>
        </Modal.Footer>
    </Modal>
);

const SuccessModal = ({ show, onHide }) => (
    <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton>
            <Modal.Title>Success! <i className="fa fa-check-circle text-primary"></i></Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <p>Your action has been completed successfully!</p>
        </Modal.Body>
    </Modal>
);
// --- END: Embedded Modal Definitions for ClientView ---


// --- START: ClientView Main Component ---

function ClientView({ onBack, client, onVisitLogged, onHouseholdUpdate }) {
    // --- HELPER FUNCTIONS FOR RESETTING STATE ---
    const getClearUpdateInfo = (clientID) => ({
        clientID: clientID,
        firstName: "", middleName: "", lastName: "", DOB: "", photo: "", email: "",
        addressLine1: "", addressLine2: "", city: "", state: "", zip: "",
        phoneNumber: "", // Clear phone number input
    });
    const getResetVisitInfo = (clientID) => ({
        visitID: "",
        date: new Date().toISOString().split("T")[0], // Current date is the default
        notes: "", reason: "", outcome: "", followUp: "", clientID: clientID, volunteerID: "",
    });
    // --- END HELPER FUNCTIONS FOR RESETTING STATE ---


    // The "source of truth" for client display data.
    const [clientInfo, setClientInfo] = useState({});
    // The state used for the EditClientModal form (autofilled data).
    const [updateInfo, setUpdateInfo] = useState(getInitialUpdateInfo(client));
    // Initial state setup for visitInfo
    const [visitInfo, setVisitInfo] = useState(getResetVisitInfo(client.clientID));
    
    const [organizationName, setOrganizationName] = useState("Loading...");
    const [isRootUser, setIsRootUser] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    
    const [serviceInfo, setServiceInfo] = useState({
        serviceID: "", serviceType: "Food Pantry", // SMART DEFAULT: "Food Pantry"
        orgName: organizationName,
    });
    // Modal state controllers
    const [showEditClientModal, setShowEditClientModal] = useState(false);
    const [showLogVisitModal, setShowLogVisitModal] = useState(false);
    const [showEditHouseholdModal, setShowEditHouseholdModal] = useState(false);

    // Error/Success state
    const [errors, setErrors] = useState({ message: "" });
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const navigate = useNavigate();

    // EFFECT: Set clientInfo and updateInfo whenever the parent 'client' prop changes
    useEffect(() => {
        if (client && client.clientID) {
            // Standardize the client data (crucial for DOB date format for form autofill)
            const standardizedClient = {
                ...client,
                DOB: client.DOB ? new Date(client.DOB).toISOString().split("T")[0] : "",
            };

            // 1. Update the client display data
            setClientInfo(standardizedClient);
            // 2. Update the form data (this is the autofill)
            setUpdateInfo(getInitialUpdateInfo(standardizedClient));
        }
    }, [client.clientID]); // Dependency array: runs ONLY when the clientID changes

    // Fetch Organization Name
    useEffect(() => {
        const fetchOrgName = async () => {
            const orgId = Cookies.get('OrgID');
            if (!orgId) { setOrganizationName("No Organization Found"); return; }
            try {
                console.log ("Fetching organization name for OrgID:", orgId);
                const url = process.env.REACT_APP_BACKURL + `/api/foodPantry/organization/${orgId}`;
                const response = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json', }, credentials: 'include' });
                if (!response.ok) { throw new Error(`Failed to fetch organization: ${response.status} ${response.statusText}`); }
                const data = await response.json();

                if (data.organization && data.organization.organizationName) {
                    const fetchedOrgName = data.organization.organizationName;
                    setOrganizationName(fetchedOrgName);
                    setServiceInfo(prev => ({ ...prev, orgName: fetchedOrgName }));
                } else { setOrganizationName("Organization name not available"); }
            } catch (error) {
                console.error("Error fetching organization data:", error);
                setOrganizationName("Error loading organization");
            }
        };
        fetchOrgName();
    }, []);

    useEffect(() => {
        const sessionID = Cookies.get("sessionID");
        if (!sessionID) {
            setOrganizationName("Session Expired");
            return;
        }

        const checkUserRoleAndFetchData = async () => {
            try {
                // 1. Check User Role
                const userResponse = await axios.get(
                    process.env.REACT_APP_BACKURL + "/api/foodPantry/user/role",
                    {
                        headers: { Authorization: `Bearer ${sessionID}`, },
                        withCredentials: true,
                    }
                );
                
                const { roleID } = userResponse.data;
                console.log("User roleID: ", roleID);
                const isSuperAdmin = roleID === "0";
                setIsRootUser(isSuperAdmin);

                if (isSuperAdmin) {
                    // Super Admin: Fetch all organizations and set default
                    fetchOrganizations(sessionID);
                    setOrganizationName("Select Organization"); // Default for super admin
                    setServiceInfo(prev => ({ ...prev, orgName: "Select Organization" })); // Default org for service
                } else {
                    // Org Admin: Fetch org name from cookie/ID
                    fetchOrgData();
                }

            } catch (error) {
                console.error("Error checking user role: ", error);
                setOrganizationName("Error loading organization");
            }
        };

        const fetchOrgData = async () => {
            const orgId = Cookies.get('OrgID');
            if (!orgId) {
                setOrganizationName("No Organization Found");
                return;
            }
            try {
                const url = process.env.REACT_APP_BACKURL + `/api/foodPantry/organization/${orgId}`;
                const response = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json', }, credentials: 'include' });
                if (!response.ok) { throw new Error(`Failed to fetch organization: ${response.status} ${response.statusText}`); }
                const data = await response.json();
                if (data.organization && data.organization.organizationName) {
                    const fetchedOrgName = data.organization.organizationName;
                    setOrganizationName(fetchedOrgName);
                    // For non-root users, only allow their current organization in the dropdown
                    setOrganizations([{
                        id: orgId,
                        name: fetchedOrgName
                    }]);
                    // Update serviceInfo with the fetched organization name for logging
                    setServiceInfo(prev => ({ ...prev, orgName: fetchedOrgName }));
                } else { setOrganizationName("Organization name not available"); }
            } catch (error) {
                console.error("Error fetching organization data:", error);
                setOrganizationName("Error loading organization");
            }
        };

        const fetchOrganizations = async (sessionID) => { 
            try {
                const response = await axios.get(
                    process.env.REACT_APP_BACKURL + "/api/foodPantry/organization",
                    {
                        headers: { Authorization: `Bearer ${sessionID}`, },
                        withCredentials: true,
                    }
                );
                const data = response.data;
                if (data?.OrgNames && Array.isArray(data.OrgNames)) {
                    console.log("Fetched organizations: ", data.OrgNames);
                    const formattedOrganizations = data.OrgNames.map((org) => ({ id: org.orgID, name: org.name, }));
                    setOrganizations(formattedOrganizations);
                } else { console.error("Error fetching organizations: ", data); }
            } catch (error) { console.error("Error fetching organizations: ", error); }
        };

        checkUserRoleAndFetchData();
    }, []); // Run only once on mount

    // Handlers
    const handleChange = (e) => {
        let { name, value } = e.target;
        setVisitInfo((prevData) => ({ ...prevData, [name]: value, }));
    };

    // Used for superAdmin to select an organization.
    const handleOrganizationChange = (e) => {
        // Only root users can change organizations
        if (isRootUser) {
            const newOrgName = e.target.value;
            setOrganizationName(newOrgName);
            // Also update the organization name in serviceInfo for logging
            setServiceInfo(prev => ({ ...prev, orgName: newOrgName }));
        }
    };

    const handleServiceChange = (e) => {
        let { name, value } = e.target;
        setServiceInfo((prevData) => ({ ...prevData, [name]: value, }));
    };
    const handleUpdate = (e) => {
        let { name, value } = e.target;
        // Phone number cleaning and truncation
        if (name === 'phoneNumber') {
            const onlyNums = value.replace(/\D/g, '');
            value = onlyNums.slice(0, 10);
        }
        setUpdateInfo((prevData) => ({ ...prevData, [name]: value, }));
    };

    // --- CLOSE HANDLERS TO RESET STATE ON HIDE/CANCEL ---

    const handleCloseEditClientModal = () => {
        // CRUCIAL: Reset ALL update fields back to the current clientInfo state (last saved data).
        if (clientInfo && clientInfo.clientID) {
            setUpdateInfo(getInitialUpdateInfo(clientInfo));
        }
        setShowEditClientModal(false);
    };
    const handleCloseLogVisitModal = () => {
        // Reset all visit fields to current date and empty strings
        setVisitInfo(getResetVisitInfo(clientInfo.clientID));
        // Reset service type to default "Food Pantry"
        setServiceInfo(prev => ({ ...prev, serviceType: "Food Pantry" }));
        setShowLogVisitModal(false);
    };

    // --- END CLOSE HANDLERS ---


    const handleSave = async (e) => {
        e.preventDefault();
        const sessionID = Cookies.get("sessionID");
        if (!sessionID) {
            alert("Session expired. Please log in again.");
            navigate("/SignIn");
            return;
        }

        // Optimistic Update & Data Validation
        const updatedClientInfo = { ...clientInfo };
        const phone = updateInfo.phoneNumber.replace(/\D/g, '');

        if (phone.length > 0 && phone.length !== 10) {
            alert("Please enter a complete 10-digit phone number, or leave the field blank.");
            return;
        }

        // Apply non-empty/valid changes to updatedClientInfo (Simplified from original logic)
        updatedClientInfo.firstName = updateInfo.firstName || updatedClientInfo.firstName;
        updatedClientInfo.lastName = updateInfo.lastName || updatedClientInfo.lastName;
        updatedClientInfo.DOB = updateInfo.DOB || updatedClientInfo.DOB;
        updatedClientInfo.email = updateInfo.email || updatedClientInfo.email;
        updatedClientInfo.addressLine1 = updateInfo.addressLine1 || updatedClientInfo.addressLine1;
        updatedClientInfo.addressLine2 = updateInfo.addressLine2 || updatedClientInfo.addressLine2;
        updatedClientInfo.city = updateInfo.city || updatedClientInfo.city;
        updatedClientInfo.state = updateInfo.state || updatedClientInfo.state;
        updatedClientInfo.zip = updateInfo.zip || updatedClientInfo.zip;

        if (phone.length === 10) {
            updatedClientInfo.areaCode = phone.slice(0, 3);
            updatedClientInfo.telPrefix = phone.slice(3, 6);
            updatedClientInfo.lineNum = phone.slice(6, 10);
        } else if (phone.length === 0) {
            // Clear phone details if the input was cleared
            updatedClientInfo.areaCode = '';
            updatedClientInfo.telPrefix = '';
            updatedClientInfo.lineNum = '';
        }

        setClientInfo(updatedClientInfo); // Apply optimistic update

        const params = {
            client: { clientID: updatedClientInfo.clientID, firstName: updatedClientInfo.firstName, middleName: updatedClientInfo.middleName, lastName: updatedClientInfo.lastName, DOB: updatedClientInfo.DOB, photo: updatedClientInfo.photo, },
            phone: { areaCode: updatedClientInfo.areaCode, telPrefix: updatedClientInfo.telPrefix, lineNum: updatedClientInfo.lineNum, },
            address: { addressLine1: updatedClientInfo.addressLine1, addressLine2: updatedClientInfo.addressLine2, city: updatedClientInfo.city, state: updatedClientInfo.state, zip: updatedClientInfo.zip, },
            user: { email: updatedClientInfo.email, },
        };
        try {
            await axios.put(process.env.REACT_APP_BACKURL + "/api/foodPantry/client", params, { headers: { Authorization: `Bearer ${sessionID}` }, withCredentials: true, });

            setShowEditClientModal(false);
            setShowSuccessModal(true);
            // RESET: Clear the input fields after successful save
            setUpdateInfo(getClearUpdateInfo(updatedClientInfo.clientID));
        } catch (error) {
            console.error("Error updating client: ", error);
            const errorMessage = error.response?.data?.message || "Error updating client";
            setErrors({ message: errorMessage });
            alert("Updating Failed: " + errorMessage);
            setClientInfo(client); // Revert on failure
        }
    };

    const handleLogVisit = async (e) => {
        e.preventDefault();
        const sessionID = Cookies.get("sessionID");
        if (!sessionID) {
            alert("Session expired. Please log in again.");
            navigate("/SignIn");
            return;
        }
        if (organizationName.includes("Error") || organizationName.includes("No Organization")) {
            alert("Organization name is not yet loaded or an error occurred. Please wait a moment and try again.");
            return;
        }

        try {
            const requestData = {
                visit: { visitID: visitInfo.visitID || uuidv4(), reason: visitInfo.reason, notes: visitInfo.notes, outcome: visitInfo.outcome, followUp: visitInfo.followUp, date: visitInfo.date, clientID: visitInfo.clientID, },
                service: { serviceID: serviceInfo.serviceID || uuidv4(), serviceType: serviceInfo.serviceType, orgName: serviceInfo.orgName, },
            };
            await axios.post(process.env.REACT_APP_BACKURL + "/api/foodPantry/visit", requestData, { headers: { Authorization: `Bearer ${sessionID}` }, withCredentials: true, });

            setShowLogVisitModal(false);
            setShowSuccessModal(true);
            // This prop calls the refresh function passed from the parent (SearchClient.js)
            if (onVisitLogged) {
                onVisitLogged();
            }

            // RESET: Clear the input fields after successful save
            setVisitInfo(getResetVisitInfo(visitInfo.clientID));
            setServiceInfo(prev => ({ ...prev, serviceType: "Food Pantry" })); // Reset service type to default
        } catch (error) {
            console.error("Error Logging Visit: ", error);
            const errorMessage = error.response?.data?.message || "Error Logging Visit";
            setErrors({ message: errorMessage });
            alert("Logging Failed: " + errorMessage);
        }
    };

    const handleCloseModal = () => {
        setShowSuccessModal(false);
        navigate("/dashboard");
    };

    let profilePic = client.photo || "./images/defaultProfilePic.avif";

    return (
        <React.Fragment>
            <div value="clientID" className="card border border-light m-0">
                <div className="card-header border-bottom border-light">
                    <h2 className="text-center mb-1 text-primary">
                        <i className="fa fa-user me-2"></i>
                        {clientInfo.firstName + " " + clientInfo.lastName}
                    </h2>
                </div>

                <div className="card-body">
                    {/* Profile Picture */}
                    <div className="d-flex justify-content-center mb-3">
                        <img
                            src={profilePic}
                            alt="profile picture"
                            style={{ width: "128px", height: "128px", objectFit: "cover" }}
                            className="rounded-circle"
                        />
                    </div>

                    {/* Client information (Main view) */}
                    <div className="row">
                        <div className="col-lg-6 col-md-12 p-2">
                            <div className="form-floating">
                                <input id="txtPhone" type="tel" placeholder="(555) 123-4567" value={formatPhoneNumber(clientInfo.areaCode, clientInfo.telPrefix, clientInfo.lineNum)} className="form-control"
                                    disabled />
                                <label htmlFor="txtPhone">Phone</label>
                            </div>
                        </div>

                        <div className="col-lg-6 col-md-12 p-2">
                            <div className="form-floating">
                                <input id="txtDOB" type="date" value={clientInfo.DOB} className="form-control" disabled />
                                <label htmlFor="txtDOB">DOB</label>
                            </div>
                        </div>
                        <div className="col-lg-12 col-md-12 p-2">
                            <div className="form-floating">
                                <input id="txtEmail" type="text" placeholder="Email" value={clientInfo.email} className="form-control" disabled />
                                <label htmlFor="txtEmail">Email</label>
                            </div>
                        </div>
                        <div className="col-lg-6 col-md-12 p-2">
                            <div className="form-floating">
                                <input id="txtStreetAddressLine1" type="text" placeholder="Street Address Line 1" value={clientInfo.addressLine1} className="form-control" disabled />
                                <label htmlFor="txtStreetAddressLine1">Street Address Line 1</label>
                            </div>
                        </div>
                        <div className="col-lg-6 col-md-12 p-2">
                            <div className="form-floating">
                                <input id="txtStreetAddressLine2" type="text" placeholder="Street Address Line 2" value={clientInfo.addressLine2} className="form-control" disabled />
                                <label htmlFor="txtStreetAddressLine2">Street Address Line 2</label>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-12 p-2">
                            <div className="form-floating">
                                <input id="txtCity" type="text" placeholder="City" value={clientInfo.city} className="form-control" disabled />
                                <label htmlFor="txtCity">City</label>
                            </div>
                        </div>

                        <div className="col-lg-4 col-md-6 p-2">
                            <div className="form-floating">
                                <input id="txtState" type="text" placeholder="State" value={clientInfo.state} className="form-control" disabled />
                                <label htmlFor="txtState">State</label>
                            </div>
                        </div>

                        <div className="col-lg-4 col-md-6 p-2">
                            <div className="form-floating">
                                <input id="txtZip" type="text" placeholder="Zip Code" value={clientInfo.zip}
                                    className="form-control" disabled />
                                <label htmlFor="txtZip">Zip Code</label>
                            </div>
                        </div>
                    </div>

                    {/* Buttons */}
                    <div className="mt-2 row g-1 d-flex justify-content-center">
                        {/* BACK BUTTON */}
                        <Button variant="danger" className="col-lg-5 col-md-5 m-2 text-nowrap" onClick={onBack}>
                            <i className="fa fa-arrow-left me-1"></i> Back
                        </Button>
                        {/* PRIMARY ACTION */}
                        <Button variant="primary" className="col-lg-5 col-md-5 m-2 text-nowrap" onClick={() => setShowLogVisitModal(true)}>
                            <i className="fa fa-book-medical me-1"></i> Log Visit
                        </Button>
                        {/* SECONDARY ACTIONS (Outline) */}
                        <Button variant="outline-primary" className="col-lg-5 col-md-5 m-2 text-nowrap" onClick={() => setShowEditHouseholdModal(true)}>
                            <i className="fa fa-users me-1"></i> Edit Household
                        </Button>
                        <Button variant="outline-primary" className="col-lg-5 col-md-5 m-2 text-nowrap" onClick={() => setShowEditClientModal(true)}>
                            <i className="fa fa-pen-to-square me-1"></i> Edit Client
                        </Button>
                    </div>
                </div>
            </div>

            {/* Modals controlled by state */}
            <EditHouseholdModal
                clientID={clientInfo.clientID}
                show={showEditHouseholdModal}
                onHide={() => setShowEditHouseholdModal(false)}
                onHouseholdUpdate={onHouseholdUpdate}
            />
            <EditClientModal
                show={showEditClientModal}
                onHide={handleCloseEditClientModal} // Use reset handler
                updateInfo={updateInfo}
                handleUpdate={handleUpdate}
                handleSave={handleSave}
            />
            <LogVisitModal
                show={showLogVisitModal}
                onHide={handleCloseLogVisitModal} // Use reset handler
                visitInfo={visitInfo}
                serviceInfo={serviceInfo}
                organizationName={organizationName}
                handleChange={handleChange}
                handleServiceChange={handleServiceChange}
                handleLogVisit={handleLogVisit}
                isRootUser={isRootUser}
                organizations={organizations}
                handleOrganizationChange={handleOrganizationChange}
            />
            <SuccessModal show={showSuccessModal} onHide={handleCloseModal} />

        </React.Fragment>
    );
}

export default ClientView;